import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { FC, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { AuthState_AuthStatus } from '@/shared/api/protocol-ts/api/auth/dto_auth_pb';
import { NEW_LINE, PATHS } from '@/shared/config';
import { useAppDispatch, useMedia } from '@/shared/hooks';
import { normalizeEmail, trackEvent } from '@/shared/lib';
import { Button, Input, Layout, Modal } from '@/shared/ui/';

import { authModel } from '@/entities/auth';
import { ModalID, modalModel } from '@/entities/modal';

import { CongratsModal } from '@/widgets/CongratsModal';
import { MobileHeader } from '@/widgets/Header';
import { ResetPasswordErrorModal } from '@/widgets/ResetPasswordErrorModal';

import {
  resetPasswordSchema,
  ResetPasswordSchemaFormPayload,
} from './config/formConfig';
import styles from './ResetPassword.module.scss';

type ResetPasswordProps = {
  className?: string;
  testID?: string;
};

export const ResetPassword: FC<ResetPasswordProps> = (props) => {
  const { className, testID } = props;

  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isMobile } = useMedia();

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValid },
  } = useForm<ResetPasswordSchemaFormPayload>({
    resolver: yupResolver(resetPasswordSchema),
    mode: 'onChange',
  });

  const openResetPasswordCongratsModalHandler = (
    email: string,
    expiredTime: number,
  ) => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.CongratsModal,
        data: {
          title: formatMessage({
            id: 'resetPassword.congrats.title',
            defaultMessage: 'Check your email',
          }),
          message: formatMessage(
            {
              id: 'resetPassword.congrats.description',
              defaultMessage:
                "We have sent password reset instructions to {email}.{ newLine }Please check your email to reset password. Don't forget to also check your spam folder in case the email got sent there.",
            },
            { email, newLine: NEW_LINE },
          ),
          redirectTo: PATHS.signIn,
          buttonText: formatMessage({
            id: 'resetPassword.congrats.button',
            defaultMessage: 'Back to Sign in',
          }),
          expiredTime,
        },
      }),
    );
  };

  const onSubmit: SubmitHandler<ResetPasswordSchemaFormPayload> = async (
    data,
  ) => {
    const email = normalizeEmail(data.email);

    try {
      const recoverByEmailResponse = await dispatch(
        authModel.thunks.recoverByEmail({ Email: email }),
      ).unwrap();

      if (recoverByEmailResponse.Status === AuthState_AuthStatus.IN_PROGRESS) {
        const expiredTime = new Date(
          recoverByEmailResponse?.ExpiresAt?.toDate() ?? 0,
        ).getTime();

        openResetPasswordCongratsModalHandler(email, expiredTime);
      }
    } catch {
      // NOTE: the rejected thunk is already surfaced by the global error toast middleware;
      // the backend never reports whether the email is registered.
    }
  };

  useEffect(() => {
    trackEvent({
      eventName: 'ResetPassword Page Viewed',
      isPageViewEvent: true,
    });
  }, []);

  if (isMobile) {
    return (
      <div>
        <MobileHeader isProtectedPages={false} />

        <div className={styles.mobileWrapper}>
          <h3 className={cn(styles.mobileTitle, 'h2m')}>
            {formatMessage({
              id: 'resetPassword.title',
              defaultMessage: 'Reset password',
            })}
          </h3>

          <p className={cn(styles.mobileSubTitle, 'p3')}>
            {formatMessage({
              id: 'resetPassword.info',
              defaultMessage:
                'Enter your email to get a link for setting a new password',
            })}
          </p>

          <form
            id="resetPasswordForm"
            onSubmit={handleSubmit(onSubmit)}
            className={styles.mobileForm}
          >
            <Controller
              control={control}
              name="email"
              render={({
                field: { ref, value, name, onBlur, onChange },
                fieldState: { error },
              }) => (
                <Input
                  className={styles.email}
                  required
                  ref={ref}
                  value={value}
                  name={name}
                  inputMode="email"
                  type="email"
                  onBlur={onBlur}
                  onChange={onChange}
                  label={formatMessage({
                    id: 'global.email',
                    defaultMessage: 'Email',
                  })}
                  error={error?.message}
                  autoComplete="email"
                />
              )}
            />
          </form>

          <footer className={styles.mobileFooter}>
            <Button
              type="submit"
              form="resetPasswordForm"
              loading={isSubmitting}
            >
              {formatMessage({
                id: 'resetPassword.reset',
                defaultMessage: 'Reset',
              })}
            </Button>

            <Button
              variant="tertiary"
              disabled={isSubmitting}
              onClick={() => navigate(PATHS.signIn)}
            >
              {formatMessage({
                id: 'global.cancel',
                defaultMessage: 'Cancel',
              })}
            </Button>
          </footer>
        </div>

        <CongratsModal />
        <ResetPasswordErrorModal />
      </div>
    );
  }

  return (
    <Layout>
      <Layout.Content>
        <Layout.Main>
          <div
            className={cn(styles.container, className)}
            data-testid={testID}
            data-section="reset-password"
          >
            <Modal
              data-component="reset-password-form"
              title={formatMessage({
                id: 'resetPassword.title',
                defaultMessage: 'Reset password',
              })}
              shouldRenderCloseIconButton={false}
              isOpen
              okButtonText={formatMessage({
                id: 'resetPassword.reset',
                defaultMessage: 'Reset',
              })}
              okButtonProps={{
                type: 'submit',
                form: 'resetPasswordForm',
                disabled: !isValid,
                loading: isSubmitting,
              }}
              cancelButtonProps={{ variant: 'tertiary' }}
              onCancel={() => navigate(PATHS.signIn)}
              shouldCloseOnOverlayClick={false}
            >
              <p className={cn(styles.info, 'p2')}>
                <FormattedMessage
                  id="resetPassword.info"
                  defaultMessage="Enter your email to get a link for setting a new password"
                />
              </p>

              <form id="resetPasswordForm" onSubmit={handleSubmit(onSubmit)}>
                <Controller
                  control={control}
                  name="email"
                  render={({
                    field: { ref, value, name, onBlur, onChange },
                    fieldState: { error },
                  }) => (
                    <Input
                      className={styles.email}
                      required
                      ref={ref}
                      value={value}
                      name={name}
                      inputMode="email"
                      type="email"
                      onBlur={onBlur}
                      onChange={onChange}
                      label={formatMessage({
                        id: 'global.email',
                        defaultMessage: 'Email',
                      })}
                      error={error?.message}
                      autoComplete="email"
                    />
                  )}
                />
              </form>
            </Modal>
          </div>
        </Layout.Main>
      </Layout.Content>

      <CongratsModal />
      <ResetPasswordErrorModal />
    </Layout>
  );
};
